import { CdkMenuModule } from '@angular/cdk/menu';
import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EdsAppMenuComponent } from '@eventim/eds2/app-menu';
import { EDS_APP_SHELL_COMPONENTS } from '@eventim/eds2/app-shell';
import { EdsBreadcrumb, EdsBreadcrumbsComponent } from '@eventim/eds2/breadcrumbs';
import { EDS_MENU_COMPONENTS, EdsMenuTriggerDirective } from '@eventim/eds2/menu';
import {
  EdsUserMenuComponent,
  EdsUserMenuLanguagePickerMenuComponent,
  EdsUserMenuThemePickerMenuComponent,
  EdsUserMenuTriggerComponent,
} from '@eventim/eds2/user-menu';
import { LogoComponent } from '../../components/logo/logo.component';
import { EdsContextSwitcherComponent } from '@eventim/eds2/context-switcher';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import AuthService from '../../auth/auth.service';
import { EdsThemeService, ThemeIcon } from '@eventim/eds2/styling-angular';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DEFAULT_LOCALES } from '../../shared/constants/default-locales';
import { filter } from 'rxjs';

@Component({
  selector: 'tlm-main-layout',
  templateUrl: './main-layout.html',
  imports: [
    EdsAppMenuComponent,
    EDS_APP_SHELL_COMPONENTS,
    EdsBreadcrumbsComponent,
    EdsUserMenuThemePickerMenuComponent,
    CdkMenuModule,
    EDS_MENU_COMPONENTS,
    EdsUserMenuComponent,
    EdsMenuTriggerDirective,
    EdsUserMenuTriggerComponent,
    EdsUserMenuLanguagePickerMenuComponent,
    LogoComponent,
    EdsContextSwitcherComponent,
    RouterOutlet,
    TranslatePipe,
  ],
})
export class MainLayout {
  protected readonly authService = inject(AuthService);
  protected readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  readonly locales = DEFAULT_LOCALES;

  private readonly currentLang = signal(localStorage.getItem('locale') ?? 'fr-FR');
  private readonly currentUrl = signal(this.router.url.split('?')[0]);

  appMenuItems = computed(() => {
    this.authService.currentUser();
    this.currentLang();
    return this.buildAppMenuItems();
  });

  readonly breadCrumbs = computed((): EdsBreadcrumb[] => {
    const url = this.currentUrl();

    if (url === '/') {
      return [{ routerLink: '/', label: this.translate.instant('home.title'), icon: 'category' }];
    }

    const iconMap: Record<string, string> = {
      '/users': 'person',
      '/roles': 'security',
      '/logos': 'image',
      '/backgrounds': 'wallpaper',
      '/ticket-layouts': 'confirmation_number',
    };

    for (const item of this.appMenuItems()) {
      if (!item.routerLink || item.routerLink === '/') continue;
      if (!url.startsWith(item.routerLink)) continue;

      const icon = iconMap[item.routerLink];
      const subMatch = item.subItems?.find((sub) => url === sub.routerLink);
      if (subMatch && subMatch.routerLink !== item.routerLink) {
        return [
          { label: item.pageTitle, routerLink: item.routerLink, icon },
          { label: subMatch.pageTitle, routerLink: subMatch.routerLink },
        ];
      }
      return [{ label: subMatch?.pageTitle ?? item.pageTitle, routerLink: item.routerLink, icon }];
    }

    return [];
  });

  constructor() {
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ lang }) => {
      this.currentLang.set(lang);
    });
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ urlAfterRedirects }) => {
        this.currentUrl.set(urlAfterRedirects.split('?')[0]);
      });
  }

  private buildAppMenuItems() {
    return [
      {
        pageTitle: this.translate.instant('home.title'),
        routerLink: '/',
      },
      {
        pageTitle: this.translate.instant('user.title'),
        routerLink: '/users',
        roles: ['user.read', 'user.create'],
        subItems: [
          {
            pageTitle: this.translate.instant('user.list.title'),
            routerLink: '/users',
            roles: ['user.read'],
          },
          {
            pageTitle: this.translate.instant('user.create.title'),
            routerLink: '/users/create',
            roles: ['user.create'],
          },
        ],
      },
      {
        pageTitle: this.translate.instant('role.title'),
        routerLink: '/roles',
        roles: ['role.read', 'role.create'],
        subItems: [
          {
            pageTitle: this.translate.instant('role.list.title'),
            routerLink: '/roles',
            roles: ['role.read'],
          },
          {
            pageTitle: this.translate.instant('role.create.title'),
            routerLink: '/roles/create',
            roles: ['role.create'],
          },
        ],
      },
      {
        pageTitle: this.translate.instant('logo.title'),
        routerLink: '/logos',
        roles: ['logo.read', 'logo.create', 'logo.update', 'logo.delete'],
        subItems: [
          {
            pageTitle: this.translate.instant('logo.list.title'),
            routerLink: '/logos',
            roles: ['logo.read'],
          },
          {
            pageTitle: this.translate.instant('logo.create.title'),
            routerLink: '/logos/create',
            roles: ['logo.create'],
          },
        ],
      },
      {
        pageTitle: this.translate.instant('background.title'),
        routerLink: '/backgrounds',
        roles: ['background.read', 'background.create', 'background.update', 'background.delete'],
        subItems: [
          {
            pageTitle: this.translate.instant('background.list.title'),
            routerLink: '/backgrounds',
            roles: ['background.read'],
          },
          {
            pageTitle: this.translate.instant('background.create.title'),
            routerLink: '/backgrounds/create',
            roles: ['background.create'],
          },
        ],
      },
      {
        pageTitle: this.translate.instant('ticketLayout.title'),
        routerLink: '/ticket-layouts',
        roles: ['ticketLayout.read', 'ticketLayout.create'],
        subItems: [
          {
            pageTitle: this.translate.instant('ticketLayout.list.title'),
            routerLink: '/ticket-layouts',
            roles: ['ticketLayout.read'],
          },
          {
            pageTitle: this.translate.instant('ticketLayout.create.title'),
            routerLink: '/ticket-layouts/create',
            roles: ['ticketLayout.create'],
          },
        ],
      },
    ]
      .filter(
        (item) => !item.roles || item.roles.length === 0 || this.authService.hasAnyRole(item.roles),
      )
      .map((item) => ({
        ...item,
        subItems: item.subItems?.filter(
          (subItem) =>
            !subItem.roles ||
            subItem.roles.length === 0 ||
            this.authService.hasAnyRole(subItem.roles),
        ),
      }));
  }

  themeService = inject(EdsThemeService);
  themeIcon = ThemeIcon;
}
